import SortedTable, {
  type SortedTableColumn,
  type SortedTableRow,
} from '@hellodarwin/app-core/lib/components/common/sort-table';
import Button from '@hellodarwin/core/lib/components/common/button';
import HdTag from '@hellodarwin/core/lib/components/common/hd-tag';
import type {
  ClientGamePlanCycleListItem,
  GamePlanCycleStatus,
} from '@hellodarwin/core/lib/features/entities';
import { getStringDate } from '@hellodarwin/core/lib/features/helpers';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Download from '@hellodarwin/icons/dist/icons/Download';
import PDF from '@hellodarwin/icons/dist/icons/PDF';
import View from '@hellodarwin/icons/dist/icons/View';
import { useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../app/app-hooks';
import { downloadAsset } from '../../../features/api/slices/assets-slice';
import { selectProjectsIsLoading } from '../../../features/api/slices/projects-slice';
import { useNewClientApi } from '../../../features/api/use-client-api';
import {
  GamePlanActions,
  GamePlanCell,
  GamePlanIdentity,
  GamePlanPrimaryText,
  GamePlanSecondaryText,
  GamePlanTableSection,
} from './styles';

interface GamePlanFilesListProps {
  gamePlans: ClientGamePlanCycleListItem[];
}

const GamePlanFilesList = ({ gamePlans }: GamePlanFilesListProps) => {
  const dispatch = useAppDispatch();
  const api = useNewClientApi();
  const { selectedLocale } = useLocale();
  const { t } = useTranslations();
  const theme = useTheme();
  const isLoading = useAppSelector((state) =>
    selectProjectsIsLoading(state, 'fetchGamePlans'),
  );
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const downloadPublishedAsset = useCallback(
    async (cycle: ClientGamePlanCycleListItem) => {
      const assetID = cycle.latest_published_asset_id;
      if (!assetID) return;

      setPendingAction(cycle.game_plan_cycle_id);
      try {
        const { link } = await dispatch(
          downloadAsset({ api, asset_id: assetID }),
        ).unwrap();
        window.open(link, 'download');
      } catch (error) {
        console.error(error);
      } finally {
        setPendingAction(null);
      }
    },
    [api, dispatch],
  );

  const columns = useMemo<SortedTableColumn[]>(
    () => [
      {
        key: 'cycle',
        label: t('game-plan-cycles|cycle'),
        width: '36%',
      },
      {
        key: 'status',
        label: t('game-plan-cycles|status'),
        width: '20%',
      },
      {
        key: 'published',
        label: t('game-plan-cycles|published_on'),
        width: '20%',
      },
      {
        key: 'actions',
        label: t('game-plan-cycles|actions'),
        width: '24%',
        sortable: false,
      },
    ],
    [t],
  );

  const rows = useMemo<SortedTableRow[]>(() => {
    const statusColors: Record<GamePlanCycleStatus, string> = {
      draft: theme.colors.yellow_1,
      proposed: theme.colors.purple_4,
      active: theme.colors.green_1,
      archived: theme.colors.grey_4,
      superseded: theme.colors.grey_4,
      unassigned: theme.colors.grey_5,
    };

    return gamePlans.map((cycle) => {
      const cycleLabel =
        cycle.cycle_number == null
          ? t('game-plan-cycles|cycle_unassigned')
          : t('game-plan-cycles|cycle_number', {
              number: cycle.cycle_number,
            });
      const creationLabel =
        cycle.status === 'proposed'
          ? t('game-plan-cycles|description_proposed')
          : cycle.status === 'active'
            ? t('game-plan-cycles|description_active')
            : cycle.status === 'archived' || cycle.status === 'superseded'
              ? t('game-plan-cycles|description_archived')
              : t(`game-plan-cycles|creation_${cycle.creation_kind}`);
      const statusLabel = t(`game-plan-cycles|status_${cycle.status}`);
      const publishedAt = cycle.latest_published_at
        ? getStringDate(new Date(cycle.latest_published_at), selectedLocale)
        : t('game-plan-cycles|empty');
      const hasAsset = Boolean(cycle.latest_published_asset_id);

      return {
        key: cycle.game_plan_cycle_id,
        metadata: cycle,
        sortValues: [
          cycle.cycle_number ?? null,
          statusLabel,
          cycle.latest_published_at ?? '',
          null,
        ],
        cells: [
          <GamePlanIdentity key={`${cycle.game_plan_cycle_id}-cycle`}>
            <PDF size={theme.spacing.xl} />
            <GamePlanCell>
              <GamePlanPrimaryText>{cycleLabel}</GamePlanPrimaryText>
              <GamePlanSecondaryText>{creationLabel}</GamePlanSecondaryText>
            </GamePlanCell>
          </GamePlanIdentity>,
          <HdTag
            key={`${cycle.game_plan_cycle_id}-status`}
            text={statusLabel}
            color={statusColors[cycle.status]}
            withBorder
          />,
          <GamePlanPrimaryText key={`${cycle.game_plan_cycle_id}-published`}>
            {publishedAt}
          </GamePlanPrimaryText>,
          <GamePlanActions key={`${cycle.game_plan_cycle_id}-actions`}>
            <Button
              className="game-plan-view-button"
              fitContent
              size="small"
              defaultStyle={theme.colors.white_1}
              headingIcon={<View size={theme.spacing.md} />}
              disabled={!hasAsset || pendingAction !== null}
              onClick={() => {
                window.open(
                  `/game-plan/pdf/${cycle.latest_published_asset_id}`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            >
              {t('game-plan-cycles|view')}
            </Button>
            <Button
              fitContent
              size="small"
              defaultStyle={theme.colors.purple_1}
              headingIcon={<Download size={theme.spacing.md} />}
              disabled={!hasAsset || pendingAction !== null}
              loading={pendingAction === cycle.game_plan_cycle_id}
              onClick={() => void downloadPublishedAsset(cycle)}
            >
              {t('game-plan-cycles|download')}
            </Button>
          </GamePlanActions>,
        ],
      };
    });
  }, [
    downloadPublishedAsset,
    gamePlans,
    pendingAction,
    selectedLocale,
    t,
    theme,
  ]);

  return (
    <GamePlanTableSection>
      <SortedTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        emptyLabel={t('game-plan-cycles|empty_table')}
        naturalHeight
        horizontalScroll
        compactRows
      />
    </GamePlanTableSection>
  );
};

export default GamePlanFilesList;
